import { memo, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Image,
  ScrollView,
  Text,
} from '@bonanza-social/moonlight';
import { RegistrationStepProps } from '../../RegistrationStep.types';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import {
  selectReferralRegistrationOffer,
  updateRegistrationData,
} from '../../../../state';
import { Trans, useTranslation } from '@bonanza-social/i18n';
import { StackNavigationProp, useNavigation } from '@bonanza-social/navigation';
import { formatCompact } from '@bonanza-social/toolkit/utils';
import { rewardTargetMap } from '../../../../utils/formatRewardTarget';
import { useStickyButton } from '../../../../hooks/useStickyButton';

function IntroStep({ onSubmit }: RegistrationStepProps) {
  const promo = useSelector(selectReferralRegistrationOffer());
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const dispatch = useDispatch();

  const { t } = useTranslation('registration');

  const { handleContentLayout, handleScrollViewLayout, scrollHandler } =
    useStickyButton({
      buttonHeight: 120,
      extraOffset: 76,
    });

  const submitIntroStep = useCallback(() => {
    if (promo) {
      dispatch(
        updateRegistrationData({
          firstStep: true,
        }),
      );
    }
    onSubmit();
  }, [dispatch, onSubmit, promo]);

  const termsComponents = useMemo(
    () => ({
      textLink: (
        <Text
          variant="link-small"
          onPress={() => navigation.navigate('RAFTerms')}
        />
      ),
    }),
    [navigation],
  );

  if (!promo) return null;

  return (
    <Box
      padding="space-300"
      flex={1}
      width={{
        mobile: '100%',
        tablet: '100%',
        desktop: 760,
      }}
      margin="auto"
      height="100%"
      justifyContent="space-between"
      marginBottom="safe-area-inset-bottom"
      onLayout={handleScrollViewLayout}
    >
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ flex: 1 }}
        animated={true}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
      >
        <Box
          gap="space-400"
          marginBottom="space-400"
          onLayout={handleContentLayout}
        >
          <Image
            source={promo.bannerImageUrl}
            borderTopLeftRadius="radius-400"
            borderTopRightRadius="radius-400"
            width="100%"
            aspectRatio={2}
            contentFit="cover"
            testID={`intro-step-promoId:${promo.promotionId}`}
          />
          <Text variant="h5" gradient="darkSurfaceAccent">
            {t(
              promo.rewardDescription
                ? 'introStepTitle'
                : 'introStepTitleOnlyBagged',
              {
                referrerName: promo.subTypeMeta?.referrerName,
                amount: formatCompact(promo.rewards[0].rewardAmount),
                target: rewardTargetMap.get(promo.rewards[0].rewardTarget),
                description: promo.rewardDescription,
              },
            )}
          </Text>
        </Box>
      </ScrollView>
      <Box gap="space-200" paddingTop="space-200">
        <Button
          variant="primary"
          locNS="registration"
          locKey="getStarted"
          rightIcon="arrowForward"
          onPress={submitIntroStep}
        ></Button>
        <Text variant="body-s" color="darkSurfacePrimary">
          <Trans
            i18nKey="termsAndConditions"
            ns="registration"
            components={termsComponents}
          />
        </Text>
      </Box>
    </Box>
  );
}

export default memo(IntroStep);
